import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getLandingInsights, getStoreInsights, getTrafficInsights } from '../../api/growthAnalytics';
import { getSignupsTimeseries } from '../../api/authAdmin';
import { useAuth } from '../../context/AuthContext';
import { useApiEnvironment } from '../../context/ApiEnvironmentContext';
import { GROWTH_PLATFORMS } from '../../utils/growthAnalytics';
import { signupReport } from '../../utils/acquisitionFunnel';

const growthOptions = {
  staleTime: 5 * 60 * 1000,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
  retry: false,
};

export default function useGrowthData({ from, to, platform = 'all', entry = 'landing', locale = 'all', enabled = true, onError }) {
  const { token, cacheSession } = useAuth();
  const { apiEnvironment } = useApiEnvironment();
  const store = useQuery({
    ...growthOptions,
    queryKey: ['growth', apiEnvironment, cacheSession, from, to, platform],
    queryFn: ({ signal }) => getStoreInsights(from, to, platform, token, signal),
    enabled: !!token && cacheSession != null && enabled,
  });
  const landing = useQuery({
    ...growthOptions,
    queryKey: ['growth-landing', apiEnvironment, cacheSession, from, to, entry],
    queryFn: ({ signal }) => getLandingInsights(from, to, token, signal, entry),
    enabled: !!token && cacheSession != null && enabled,
  });
  const signups = useQuery({
    ...growthOptions,
    queryKey: ['growth-signups', apiEnvironment, cacheSession, from, to],
    queryFn: async () => signupReport(await getSignupsTimeseries(from, to, token), from, to),
    enabled: !!token && cacheSession != null && enabled,
  });
  const traffic = useQuery({
    ...growthOptions,
    queryKey: ['growth-traffic', apiEnvironment, cacheSession, from, to, entry, locale],
    queryFn: ({ signal }) => getTrafficInsights(from, to, entry, locale, token, signal),
    enabled: !!token && cacheSession != null && enabled && ['landing', 'home'].includes(entry),
  });
  const expired = [store.error, landing.error, signups.error, traffic.error].find((error) => error?.status === 401);
  useEffect(() => {
    if (expired && onError) onError(expired);
  }, [expired, onError]);
  return {
    byId: { store, landing, signups, traffic }, platforms: platform === 'all' ? GROWTH_PLATFORMS : [platform], apiEnvironment,
    fetching: store.isFetching || landing.isFetching || signups.isFetching || traffic.isFetching,
    refresh: () => Promise.allSettled([store.refetch(), landing.refetch(), signups.refetch(), ...(['landing', 'home'].includes(entry) ? [traffic.refetch()] : [])]),
  };
}
